import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { API_BASE, payments as paymentsApi } from '../../lib/api';
import { mergeDeep, cloneDeep, formatMessage } from '../../utils/i18n';

function formatCurrency(amount, currency) {
  const formatter = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency || 'USD',
  });
  return formatter.format(Number(amount || 0));
}

const DEFAULT_COPY = {
  title: 'Payments & Discounts',
  loading: 'Loading payment details…',
  buttons: {
    close: 'Close',
  },
  summary: {
    outstanding: 'Outstanding Balance',
    approved: 'Approved Payments',
    referralEarnings: 'Referral Earnings',
    referralPending: 'Referral Pending',
  },
  referral: {
    heading: 'Referral Discounts',
    own: {
      heading: 'Your Referral Code',
      generateNew: 'Generate Referal Code',
      generate: 'Generate Referal Code',
    },
    apply: {
      heading: 'Apply a Referral Code',
      placeholder: 'REF-XXXX',
      providerInfo: '',
      providerFallback: '',
      submit: 'Code',
    },
    messages: {
      generatedWithAmount: 'Referral code {code} generated! Share it with a new student so they can receive {amount} off their tuition.',
      generatedNoAmount: 'Referral code {code} generated! Share it with a new student.',
      applied: 'Referral code {code} applied successfully.',
    },
    errors: {
      enterCode: 'Enter a referral code to apply.',
      generateFailed: 'Failed to generate referral code.',
      applyFailed: 'Failed to apply referral code.',
    },
  },
  paymentForm: {
    heading: 'Submit Payment',
    amountLabel: 'Amount',
    methodLabel: 'Method',
    amountPlaceholder: 'Enter amount',
    submit: 'Submit for Approval',
    receiptLabel: 'Payment receipt',
    receiptDescription: '',
    receiptSelected: 'Selected file:',
    removeReceipt: 'Remove file',
    methods: [
      { value: 'Online', label: 'Online' },
      { value: 'Cash', label: 'Cash' },
      { value: 'Check', label: 'Check' },
      { value: 'Other', label: 'Other' },
    ],
    errors: {
      amountInvalid: 'Enter a valid payment amount.',
      amountExceeds: 'Amount exceeds remaining balance.',
      submitFailed: 'Failed to submit payment.',
    },
  },
  history: {
    heading: 'History',
    empty: 'No payments submitted yet.',
    columns: {
      date: 'Date',
      amount: 'Amount',
      method: 'Method',
      status: 'Status',
      notes: 'Notes',
      receipt: 'Receipt',
    },
    statusLabels: {
      APPROVED: 'Approved',
      PENDING: 'Pending',
      REJECTED: 'Rejected',
    },
    downloadReceipt: 'Download receipt',
    previewReceipt: 'Preview receipt',
  },
  preview: {
    title: 'Receipt Preview',
    close: 'Close Preview',
    unsupported: 'Preview unavailable. Download the file to view it.',
    loading: 'Loading receipt preview…',
    error: 'Failed to load receipt preview.',
  },
  messages: {
    paymentSubmitted: 'Payment submitted for approval.',
  },
  errors: {
    load: 'Failed to load payment details.',
  },
};

export default function StudentPaymentsModal({ user, onClose, onSummaryChange, copy: copyOverrides }) {
  const copy = useMemo(() => mergeDeep(cloneDeep(DEFAULT_COPY), copyOverrides || {}), [copyOverrides]);
  const fileInputRef = useRef(null);
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('Online');
  const [referralCodeInput, setReferralCodeInput] = useState('');
  const [paymentReceipt, setPaymentReceipt] = useState(null);
  const [previewData, setPreviewData] = useState({
    open: false,
    url: '',
    mimeType: '',
    fileName: '',
    downloadUrl: '',
  });
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const previewUrlRef = useRef(null);
  const [activeTab, setActiveTab] = useState('discounts');
  const [linkCopied, setLinkCopied] = useState(false);

  const formatPaymentDate = useCallback((value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    try {
      return new Intl.DateTimeFormat('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }).format(date);
    } catch (error) {
      console.warn('Failed to format payment date:', error);
      return date.toLocaleString();
    }
  }, []);

  const notifySummaryChange = useCallback((response) => {
    if (onSummaryChange && response?.summary) {
      onSummaryChange(response.summary);
    }
  }, [onSummaryChange]);

  const getStoredToken = useCallback(() => (
    localStorage.getItem('accessToken') || localStorage.getItem('go2cdl_access_token') || ''
  ), []);

  const buildReceiptUrl = useCallback((downloadPath) => {
    if (!downloadPath) return null;
    const token = getStoredToken();
    const separator = downloadPath.includes('?') ? '&' : '?';
    const tokenQuery = token ? `${separator}token=${encodeURIComponent(token)}` : '';
    return `${API_BASE}${downloadPath}${tokenQuery}`;
  }, [getStoredToken]);

  const revokePreviewUrl = useCallback(() => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
  }, []);

  const refreshSnapshot = useCallback(async () => {
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const response = await paymentsApi.getStudentSummary();
      setSnapshot(response);
      notifySummaryChange(response);
    } catch (err) {
      console.error('Failed to load payment details:', err);
      setError(err?.message || copy.errors.load);
    } finally {
      setLoading(false);
    }
  }, [notifySummaryChange, copy]);

  useEffect(() => {
    refreshSnapshot();
  }, [refreshSnapshot]);

  const handleGenerateDiscount = useCallback(async () => {
    setMessage('');
    setError('');
    setWorking(true);
    try {
      const response = await paymentsApi.generateReferralCode();
      setSnapshot(response);
      notifySummaryChange(response);
      const code = response?.generatedReferral?.code;
      const amount = response?.generatedReferral?.amount;
      const currency = response?.settings?.currency;
      const amountText = amount !== undefined ? formatCurrency(amount, currency) : null;
      if (code) {
        const messageTemplate = amountText ? copy.referral.messages.generatedWithAmount : copy.referral.messages.generatedNoAmount;
        setMessage(formatMessage(messageTemplate, { code, amount: amountText || '' }));
      } else {
        setMessage(copy.referral.messages.generatedNoAmount);
      }
    } catch (err) {
      console.error('Failed to generate referral code:', err);
      setError(err?.message || copy.referral.errors.generateFailed);
    } finally {
      setWorking(false);
    }
  }, [notifySummaryChange, copy]);

  const handleApplyReferralCode = useCallback(async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');
    const normalized = referralCodeInput.trim().toUpperCase();
    if (!normalized) {
      setError(copy.referral.errors.enterCode);
      return;
    }

    setWorking(true);
    try {
      const response = await paymentsApi.applyReferralCode(normalized);
      setSnapshot(response);
      notifySummaryChange(response);
      setReferralCodeInput('');
      setMessage(formatMessage(copy.referral.messages.applied, { code: normalized }));
    } catch (err) {
      console.error('Failed to apply referral code:', err);
      setError(err?.message || copy.referral.errors.applyFailed);
    } finally {
      setWorking(false);
    }
  }, [notifySummaryChange, referralCodeInput, copy]);

  const clearReceiptSelection = useCallback(() => {
    setPaymentReceipt(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleReceiptChange = useCallback((event) => {
    const file = event?.target?.files?.[0] || null;
    setPaymentReceipt(file || null);
  }, []);

  const handleSubmitPayment = useCallback(async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');

    const numericAmount = Number(paymentAmount);
    if (Number.isNaN(numericAmount) || numericAmount <= 0) {
      setError(copy.paymentForm.errors.amountInvalid);
      return;
    }

    const outstanding = snapshot?.summary?.outstanding ?? 0;
    if (numericAmount > outstanding + 0.01) {
      setError(copy.paymentForm.errors.amountExceeds);
      return;
    }

    setWorking(true);
    try {
      const response = await paymentsApi.submitPayment({
        amount: numericAmount,
        method: paymentMethod,
        receipt: paymentReceipt,
      });
      setSnapshot(response);
      notifySummaryChange(response);
      setPaymentAmount('');
      clearReceiptSelection();
      setMessage(copy.messages.paymentSubmitted);
    } catch (err) {
      console.error('Failed to submit payment:', err);
      setError(err?.message || copy.paymentForm.errors.submitFailed);
    } finally {
      setWorking(false);
    }
  }, [paymentAmount, paymentMethod, paymentReceipt, notifySummaryChange, copy, clearReceiptSelection]);

  const handleClosePreview = useCallback(() => {
    revokePreviewUrl();
    setPreviewData({ open: false, url: '', mimeType: '', fileName: '', downloadUrl: '' });
    setPreviewError('');
    setPreviewLoading(false);
  }, [revokePreviewUrl]);

  const handlePreviewReceipt = useCallback(async (payment) => {
    if (!payment?.receipt?.downloadPath) return;
    revokePreviewUrl();
    setPreviewError('');
    setPreviewLoading(true);
    const downloadUrl = buildReceiptUrl(payment.receipt.downloadPath);
    setPreviewData({
      open: true,
      url: '',
      mimeType: payment.receipt.mimeType || '',
      fileName: payment.receipt.fileName || 'payment-receipt',
      downloadUrl,
    });

    try {
      const response = await fetch(downloadUrl);
      if (!response.ok) {
        const text = await response.text().catch(() => null);
        throw new Error(text || copy.preview.error);
      }
      const blob = await response.blob();
      const mimeType = response.headers.get('content-type') || payment.receipt.mimeType || 'application/octet-stream';
      const objectUrl = URL.createObjectURL(blob);
      previewUrlRef.current = objectUrl;
      setPreviewData({
        open: true,
        url: objectUrl,
        mimeType,
        fileName: payment.receipt.fileName || 'payment-receipt',
        downloadUrl,
      });
    } catch (err) {
      console.error('Failed to load receipt preview:', err);
      setPreviewError(err?.message || copy.preview.error);
    } finally {
      setPreviewLoading(false);
    }
  }, [buildReceiptUrl, copy.preview.error, revokePreviewUrl]);

  const settings = snapshot?.settings || {
    currency: 'USD',
    defaultAmount: 0,
    discountAmount: 0,
    referralLevel2Amount: 0,
    referralLevel3Amount: 0,
  };
  const summary = useMemo(() => {
    const base = snapshot?.summary || {};
    return {
      approvedTotal: base.approvedTotal || 0,
      pendingTotal: base.pendingTotal || 0,
      discountValue: base.discountValue || 0,
      outstanding: base.outstanding || 0,
      effectiveTotal: base.effectiveTotal || 0,
      referralApproved: base.referralApproved || 0,
      referralPending: base.referralPending || 0,
    };
  }, [snapshot]);

  useEffect(() => {
    notifySummaryChange(snapshot);
  }, [snapshot, notifySummaryChange]);

  const outstandingBalance = summary.outstanding || 0;
  const referralCode = snapshot?.generatedReferral || null;
  const appliedDiscount = snapshot?.profile?.appliedDiscount || null;
  const payments = snapshot?.payments || [];
  const referralRegistrationUrl = useMemo(() => {
    if (!referralCode?.code || typeof window === 'undefined') return '';
    const url = new URL('/', window.location.origin);
    url.searchParams.set('ref', referralCode.code);
    return url.toString();
  }, [referralCode?.code]);
  const referralQrUrl = referralRegistrationUrl
    ? `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(referralRegistrationUrl)}`
    : '';

  const handleCopyReferralLink = useCallback(async () => {
    if (!referralRegistrationUrl) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(referralRegistrationUrl);
      } else {
        const input = document.createElement('textarea');
        input.value = referralRegistrationUrl;
        input.style.position = 'fixed';
        input.style.opacity = '0';
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
      }
      setLinkCopied(true);
      window.setTimeout(() => setLinkCopied(false), 2000);
    } catch (copyError) {
      console.error('Failed to copy referral link:', copyError);
      setError('Could not copy the referral link. Please try again.');
    }
  }, [referralRegistrationUrl]);

  const paymentMethods = copy.paymentForm?.methods || DEFAULT_COPY.paymentForm.methods;
  const paymentMethodLabels = useMemo(() => {
    const map = {};
    paymentMethods.forEach(({ value, label }) => {
      map[value] = label;
    });
    return map;
  }, [paymentMethods]);

  const paymentContent = (
    <div className="space-y-5 sm:space-y-6">
      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 px-5 py-5 sm:px-7 sm:py-6">
          <h2 className="text-xl font-bold text-[#071a42]">Make a Payment</h2>
        </div>

        <div className="grid border-b border-slate-100 sm:grid-cols-2">
          <div className="px-5 py-4 sm:px-7">
            <div className="flex items-center gap-2 text-xs font-semibold text-slate-500"><span className="h-2.5 w-2.5 rounded-full bg-orange-500" />Amount Due</div>
            <p className={`mt-1 text-2xl font-bold ${outstandingBalance > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{formatCurrency(outstandingBalance, settings.currency)}</p>
          </div>
          <div className="border-t border-slate-100 px-5 py-4 sm:border-l sm:border-t-0 sm:px-7">
            <div className="flex items-center gap-2 text-xs font-semibold text-slate-500"><span className="h-2.5 w-2.5 rounded-full bg-emerald-600" />Approved</div>
            <p className="mt-1 text-2xl font-bold text-emerald-700">{formatCurrency(summary.approvedTotal, settings.currency)}</p>
          </div>
        </div>

        <form onSubmit={handleSubmitPayment} className="space-y-5 px-5 py-5 sm:px-7 sm:py-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="payment-amount" className="mb-2 block text-sm font-semibold text-slate-700">Payment Amount</label>
              <div className="relative"><span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4 text-slate-500">$</span><input id="payment-amount" type="number" min="0" max={Math.max(0, outstandingBalance)} step="0.01" value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)} className="w-full rounded-xl border border-slate-200 bg-white py-3 pl-8 pr-4 text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100" placeholder="0.00" aria-label={copy.paymentForm.amountLabel} disabled={working} /></div>
            </div>
            <div>
              <label htmlFor="payment-method" className="mb-2 block text-sm font-semibold text-slate-700">Payment Method</label>
              <select id="payment-method" value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100" aria-label={copy.paymentForm.methodLabel} disabled={working}>
                {paymentMethods.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}
              </select>
            </div>
          </div>

          <div>
            <span className="mb-2 block text-sm font-semibold text-slate-700">Payment Receipt <span className="font-normal text-slate-400">(optional)</span></span>
            <input id="payment-receipt" ref={fileInputRef} type="file" accept="image/*,application/pdf" onChange={handleReceiptChange} disabled={working} className="sr-only" />
            {paymentReceipt ? (
              <div className="flex flex-col gap-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex min-w-0 items-center gap-3"><ReceiptIcon className="h-6 w-6 shrink-0 text-blue-600" /><div className="min-w-0"><p className="text-sm font-semibold text-[#071a42]">Receipt ready to upload</p><p className="truncate text-sm text-slate-500">{paymentReceipt.name}</p></div></div>
                <button type="button" onClick={clearReceiptSelection} className="self-start text-sm font-semibold text-red-600 hover:text-red-700 sm:self-auto">Remove</button>
              </div>
            ) : (
              <label htmlFor="payment-receipt" className="flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 px-4 py-7 text-center transition hover:border-blue-400 hover:bg-blue-50/50">
                <UploadIcon className="h-8 w-8 text-[#1769ff]" /><span className="mt-2 text-sm font-semibold text-[#071a42]">Drop receipt here or <span className="text-[#1769ff]">Browse</span></span><span className="mt-1 text-xs text-slate-500">PDF, JPG or PNG · Max 10 MB</span>
              </label>
            )}
          </div>

          <button type="submit" disabled={working || outstandingBalance <= 0} className="flex w-full items-center justify-center rounded-xl bg-[#1769ff] px-5 py-3.5 text-sm font-bold text-white shadow-sm transition hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-200 disabled:cursor-not-allowed disabled:bg-slate-300">
            {working ? 'Submitting…' : copy.paymentForm.submit}
          </button>
        </form>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-7">
        <h2 className="text-lg font-bold text-[#071a42]">Payment History</h2>
        {payments.length === 0 ? (
          <div className="flex flex-col items-center px-4 py-10 text-center"><div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100"><ReceiptIcon className="h-7 w-7 text-slate-500" /></div><p className="mt-4 text-sm font-semibold text-[#071a42]">No payments submitted yet</p></div>
        ) : (
          <>
            <div className="hidden overflow-x-auto md:block"><table className="w-full min-w-[680px] text-left text-sm"><thead className="border-b border-slate-100 text-xs uppercase tracking-wide text-slate-500"><tr><th className="pb-3">{copy.history.columns.date}</th><th className="pb-3">{copy.history.columns.amount}</th><th className="pb-3">{copy.history.columns.method}</th><th className="pb-3">{copy.history.columns.status}</th><th className="pb-3">Receipt</th></tr></thead><tbody className="divide-y divide-slate-100">{[...payments].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).map((payment) => <tr key={payment.id} className="text-slate-600"><td className="py-4">{formatPaymentDate(payment.createdAt)}</td><td className="py-4 font-semibold text-[#071a42]">{formatCurrency(payment.amount, settings.currency)}</td><td className="py-4">{paymentMethodLabels[payment.method] || payment.method}</td><td className="py-4"><PaymentStatus status={payment.status} label={copy.history.statusLabels[payment.status] || payment.status} /></td><td className="py-4">{payment.receipt?.downloadPath ? <div className="flex gap-2"><button type="button" onClick={() => handlePreviewReceipt(payment)} className="rounded-lg border border-blue-200 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-50">Preview</button><a href={buildReceiptUrl(payment.receipt.downloadPath)} target="_blank" rel="noopener noreferrer" className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50">Download</a></div> : <span className="text-slate-400">—</span>}</td></tr>)}</tbody></table></div>
            <div className="space-y-3 md:hidden">{[...payments].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).map((payment) => <article key={payment.id} className="rounded-xl border border-slate-200 p-4"><div className="flex items-start justify-between gap-3"><div><p className="font-bold text-[#071a42]">{formatCurrency(payment.amount, settings.currency)}</p><p className="mt-1 text-xs text-slate-500">{formatPaymentDate(payment.createdAt)} · {paymentMethodLabels[payment.method] || payment.method}</p></div><PaymentStatus status={payment.status} label={copy.history.statusLabels[payment.status] || payment.status} /></div>{payment.receipt?.downloadPath && <div className="mt-3 flex gap-2"><button type="button" onClick={() => handlePreviewReceipt(payment)} className="rounded-lg border border-blue-200 px-3 py-1.5 text-xs font-semibold text-blue-700">Preview</button><a href={buildReceiptUrl(payment.receipt.downloadPath)} target="_blank" rel="noopener noreferrer" className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600">Download</a></div>}</article>)}</div>
          </>
        )}
      </section>
    </div>
  );

  const discountContent = (
    <div className="space-y-5 sm:space-y-6">
      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-7">
        <h2 className="text-xl font-bold text-[#071a42]">Invite a Friend</h2>
        <div className="mt-6 grid gap-6 border-y border-slate-100 py-6 sm:grid-cols-2 sm:items-center">
          <div className="flex flex-col items-center justify-center border-b border-slate-100 pb-6 text-center sm:border-b-0 sm:border-r sm:pb-0 sm:pr-6">
            {referralCode && referralQrUrl ? <img src={referralQrUrl} alt={`QR code for referral ${referralCode.code}`} className="h-40 w-40 rounded-xl border border-slate-200 bg-white p-2 shadow-sm" /> : <div className="flex h-40 w-40 items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50"><GiftIcon className="h-12 w-12 text-slate-400" /></div>}
          </div>
          <div className="text-center"><p className="text-sm font-semibold text-[#071a42]">Your Referral Code</p><p className="mt-3 break-all text-3xl font-extrabold tracking-wide text-[#1769ff] sm:text-4xl">{referralCode?.code || 'REF-XXXX'}</p>{referralCode ? <button type="button" onClick={handleCopyReferralLink} className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#1769ff] px-4 py-3 text-sm font-bold text-white transition hover:bg-blue-700"><CopyIcon className="h-5 w-5" />{linkCopied ? 'Referral Link Copied' : 'Copy Referral Link'}</button> : <button type="button" onClick={handleGenerateDiscount} disabled={working} className="mt-4 inline-flex w-full items-center justify-center rounded-xl bg-[#1769ff] px-4 py-3 text-sm font-bold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300">{working ? 'Generating…' : 'Generate Referral Code'}</button>}</div>
        </div>
        <div className="grid gap-4 pt-5 sm:grid-cols-2"><Metric label="Pending" value={formatCurrency(summary.referralPending || 0, settings.currency)} color="orange" /><Metric label="Earned" value={formatCurrency(summary.referralApproved || 0, settings.currency)} color="green" /></div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-7">
        <h2 className="text-lg font-bold text-[#071a42]">Have a referral code?</h2>
        {appliedDiscount ? <div className="mt-4 flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4"><span className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-600 text-sm font-bold text-white">✓</span><div><p className="font-semibold text-emerald-800">Referral code applied</p><p className="mt-0.5 text-sm text-emerald-700">{appliedDiscount.code}</p></div></div> : <form onSubmit={handleApplyReferralCode} className="mt-4 flex flex-col gap-3 sm:flex-row"><input type="text" value={referralCodeInput} onChange={(e) => setReferralCodeInput(e.target.value.toUpperCase())} placeholder={copy.referral.apply.placeholder} className="min-w-0 flex-1 rounded-xl border border-slate-200 px-4 py-3 text-slate-900 uppercase outline-none transition placeholder:normal-case placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100" disabled={working} /><button type="submit" disabled={working} className="rounded-xl bg-[#1769ff] px-6 py-3 text-sm font-bold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300">{working ? 'Applying…' : 'Apply Code'}</button></form>}
      </section>
    </div>
  );

  useEffect(() => () => {
    revokePreviewUrl();
  }, [revokePreviewUrl]);

  if (loading && !snapshot) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-50 px-5">
        <div className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm"><div className="mx-auto mb-4 h-9 w-9 animate-spin rounded-full border-4 border-slate-200 border-t-[#1769ff]" /><p className="text-sm font-semibold text-[#071a42]">{copy.loading}</p><button type="button" onClick={onClose} className="mt-6 inline-flex items-center gap-2 rounded-2xl bg-slate-800 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-900" aria-label="Back to dashboard"><span className="text-lg leading-none" aria-hidden="true">←</span> Back</button></div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-50">
      <header className="sticky top-0 z-10 border-b border-slate-200 bg-white/95 backdrop-blur"><div className="mx-auto flex h-16 max-w-7xl items-center px-4 sm:h-20 sm:px-7 lg:px-10"><img src="/images/logo.jpg" alt="Go2 CDL" className="h-10 w-20 object-contain sm:h-12 sm:w-24" /></div></header>
      <main className="mx-auto w-full max-w-5xl px-4 py-7 sm:px-7 sm:py-10 lg:px-10">
        <div className="flex items-center justify-between gap-4"><h1 className="text-3xl font-bold tracking-tight text-[#061b45] sm:text-4xl">Payments &amp; Discounts</h1><button type="button" onClick={onClose} className="inline-flex shrink-0 items-center gap-2 rounded-2xl bg-slate-800 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-900" aria-label="Back to dashboard"><span className="text-lg leading-none" aria-hidden="true">←</span> Back</button></div>
        <div className="mx-auto mt-7 grid max-w-3xl grid-cols-2 overflow-hidden rounded-xl border border-slate-300 bg-white shadow-sm sm:mt-9">
          <button type="button" onClick={() => setActiveTab('payments')} className={`flex items-center justify-center gap-2 px-3 py-3.5 text-sm font-bold transition sm:text-base ${activeTab === 'payments' ? 'bg-[#1769ff] text-white' : 'text-[#071a42] hover:bg-slate-50'}`}><WalletIcon className="h-5 w-5" />Payments</button>
          <button type="button" onClick={() => setActiveTab('discounts')} className={`flex items-center justify-center gap-2 border-l border-slate-300 px-3 py-3.5 text-sm font-bold transition sm:text-base ${activeTab === 'discounts' ? 'bg-[#1769ff] text-white' : 'text-[#071a42] hover:bg-slate-50'}`}><GiftIcon className="h-5 w-5" />Referral Discounts</button>
        </div>
        <div className="mx-auto mt-6 max-w-3xl sm:mt-7">{activeTab === 'payments' ? paymentContent : discountContent}</div>
        {(message || error) && <div role="status" className={`mx-auto mt-5 max-w-3xl rounded-xl border px-4 py-3 text-sm font-medium ${error ? 'border-red-200 bg-red-50 text-red-700' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`}>{error || message}</div>}
      </main>

      {previewData.open && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 px-4 py-8">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div>
                <h3 className="text-xl font-semibold text-slate-900">{copy.preview.title}</h3>
                {previewData.fileName && (
                  <p className="text-sm text-slate-500 break-all mt-1">{previewData.fileName}</p>
                )}
              </div>
              <button
                type="button"
                onClick={handleClosePreview}
                className="text-slate-400 hover:text-slate-600 text-2xl leading-none"
                aria-label={copy.preview.close}
              >
                ×
              </button>
            </div>

            <div className="flex-1 overflow-auto bg-slate-100 p-4 flex items-center justify-center">
              {previewLoading ? (
                <div className="text-sm text-slate-600">{copy.preview.loading}</div>
              ) : previewError ? (
                <div className="text-center space-y-3">
                  <p className="text-sm text-red-600">{previewError}</p>
                  {previewData.downloadUrl && (
                    <a
                      href={previewData.downloadUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700"
                    >
                      {copy.history.downloadReceipt || copy.paymentForm.receiptLabel}
                    </a>
                  )}
                </div>
              ) : previewData.url ? (
                previewData.mimeType?.startsWith('image/') ? (
                  <img
                    src={previewData.url}
                    alt={previewData.fileName || 'Receipt preview'}
                    className="max-h-[70vh] max-w-full rounded-lg shadow"
                  />
                ) : previewData.mimeType?.includes('pdf') ? (
                  <iframe
                    title="Receipt preview"
                    src={previewData.url}
                    className="w-full h-[70vh] bg-white rounded-lg"
                  />
                ) : (
                  <div className="text-center space-y-3 text-sm text-slate-600">
                    <p>{copy.preview.unsupported}</p>
                    {previewData.downloadUrl && (
                      <a
                        href={previewData.downloadUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700"
                      >
                        {copy.history.downloadReceipt || copy.paymentForm.receiptLabel}
                      </a>
                    )}
                  </div>
                )
              ) : (
                <div className="text-center space-y-3 text-sm text-slate-600">
                  <p>{copy.preview.unsupported}</p>
                  {previewData.downloadUrl && (
                    <a
                      href={previewData.downloadUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700"
                    >
                      {copy.history.downloadReceipt || copy.paymentForm.receiptLabel}
                    </a>
                  )}
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t bg-white flex items-center justify-between">
              {previewData.downloadUrl ? (
                <a
                  href={previewData.downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 hover:text-indigo-700 font-medium"
                >
                  {copy.history.downloadReceipt || copy.paymentForm.receiptLabel}
                </a>
              ) : <span />}
              <button
                type="button"
                onClick={handleClosePreview}
                className="px-4 py-2 rounded-lg bg-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-300"
              >
                {copy.preview.close}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PaymentStatus({ status, label }) {
  const palette = status === 'APPROVED' ? 'bg-emerald-100 text-emerald-700' : status === 'REJECTED' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800';
  return <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ${palette}`}>{label}</span>;
}

function Metric({ label, value, color }) {
  const dot = color === 'green' ? 'bg-emerald-600' : 'bg-orange-500';
  return <div className="flex items-center justify-center gap-3 text-center"><span className={`h-3 w-3 rounded-full ${dot}`} /><span className="text-sm font-medium text-slate-600">{label}</span><span className="text-lg font-bold text-[#071a42]">{value}</span></div>;
}

function WalletIcon({ className = '' }) { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={className} aria-hidden="true"><path d="M3 7.5A2.5 2.5 0 0 1 5.5 5H18a3 3 0 0 1 3 3v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7.5Z" /><path d="M3 8h15a2 2 0 1 0 0-4H6" /><path d="M16 13h5" /><circle cx="16" cy="13" r=".7" fill="currentColor" /></svg>; }
function GiftIcon({ className = '' }) { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={className} aria-hidden="true"><path d="M3 10h18v10H3zM2 6h20v4H2zM12 6v14M12 6H8.5A2.5 2.5 0 1 1 11 3.5c0 1.4-1.2 2.5-2.5 2.5H12Zm0 0h3.5A2.5 2.5 0 1 0 13 3.5C13 4.9 14.2 6 15.5 6H12Z" /></svg>; }
function ReceiptIcon({ className = '' }) { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={className} aria-hidden="true"><path d="M6 3h12v18l-3-2-3 2-3-2-3 2V3Z" /><path d="M9 8h6M9 12h6" /></svg>; }
function UploadIcon({ className = '' }) { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={className} aria-hidden="true"><path d="M12 15V3m0 0L7.5 7.5M12 3l4.5 4.5M5 13v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6" /></svg>; }
function CopyIcon({ className = '' }) { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={className} aria-hidden="true"><rect x="8" y="8" width="11" height="12" rx="2" /><path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h2" /></svg>; }
